/* What lives where in the water, by frequency.
 *
 * A spectrogram tells an acoustician a great deal and a fish farmer nothing.
 * These zones are the translation layer: they say which part of the picture is
 * a boat, which part is weather, and -- the question actually being asked --
 * which part the fish can hear at all.
 *
 * ---------------------------------------------------------------------------
 * WHAT THESE ARE NOT
 *
 * They are FREQUENCY ranges, and only that. They carry no level thresholds and
 * make no claim about harm, because this system cannot support one:
 *
 *   Ground rule 5 -- uncalibrated means relative. Until a node is
 *   bench-calibrated, no report may state dB re 1 uPa or compare one site
 *   against another.
 *
 * Every statement about impact on fish -- behavioural response, masking,
 * injury -- is a statement about absolute sound pressure. On an uncalibrated
 * mission this browser has dBFS, which is a number about the recorder rather
 * than about the sea, so any threshold drawn against it would be arithmetic
 * dressed as science. A farmer told "this is above the behavioural response
 * threshold" on the strength of an uncalibrated recording has been told
 * something false with an authority it has not earned.
 *
 * Frequency, by contrast, is a property of the signal and survives the missing
 * calibration intact: a 100 Hz tonal is 100 Hz whether or not we know how loud
 * it was. So the zones say WHAT and WHERE, never HOW BAD. When
 * `calibration.absolute` becomes true the level half can be built, and this is
 * the file it belongs in.
 *
 * ---------------------------------------------------------------------------
 * SOURCES AND UNCERTAINTY
 *
 * The ranges below are indicative, drawn from the general bioacoustics
 * literature on North Atlantic species and vessel noise. They are approximate
 * by nature: hearing thresholds vary with individual, temperature, life stage
 * and measurement method, and published ranges disagree at the edges. They are
 * here to orient a reader, not to support a regulatory finding, and the UI says
 * so rather than implying a precision nobody has.
 *
 * Anything used in a customer-facing report should be checked against a cited
 * source for that species and that jurisdiction.
 */

export interface Band {
  /** Lower and upper edge in Hz. */
  lo: number
  hi: number
  /** Short label drawn on the chart. */
  label: string
  /** What a person who is not an acoustician should take from it. */
  meaning: string
  kind: 'hearing' | 'source'
}

/* Hearing ranges: what the animals in and around a salmon pen can detect.
 *
 * Salmon are hearing generalists -- no swim bladder coupling to the inner ear
 * -- so their useful range is narrow and low, and almost all of it overlaps
 * the band that vessels are loudest in. That overlap is the single most useful
 * thing this chart can show a farmer, and it is why these two are drawn
 * together rather than on separate views. */
export const HEARING: Band[] = [
  {
    /* Below hearing, and not below noticing.
     *
     * The audiogram literature this file otherwise draws on measures acoustic
     * pressure detection and starts around 100 Hz. Infrasound is a different
     * question with a different answer: 5-10 Hz produces measurable
     * physiological arousal in juvenile salmon -- heart rate among it -- and
     * clear behavioural avoidance, without the fish "hearing" it in the sense
     * an audiogram means.
     *
     * Kept in HEARING rather than given a category of its own, because what
     * the list is for is "what reaches the animal", and this reaches it. The
     * meaning text is what stops it being read as an extension of the
     * audiogram.
     *
     * Two things follow for anything drawing this band, and both are stated to
     * the listener rather than left implicit:
     *
     *   - nobody will hear it. Human hearing stops near 20 Hz and no earphone
     *     or laptop speaker reproduces 5-10 Hz at any level. A preset that
     *     filters to this band and offers no picture would be a control that
     *     produces silence on every device a farm owns.
     *   - the sensor is not calibrated here. The A5 is +/-4 dB only from
     *     20 Hz up, so how MUCH is at 7 Hz is not a question this equipment
     *     can answer. That whether-and-where survives the missing calibration
     *     is the whole argument at the top of this file. */
    lo: 5, hi: 10, label: 'Infrasound',
    kind: 'hearing',
    meaning: 'Below hearing, and not below noticing. Signals at 5–10 Hz are '
      + 'documented to trigger physiological arousal — heart rate among it — '
      + 'and clear avoidance behaviour in juvenile salmon, without being heard '
      + 'in the sense an audiogram measures. You will not hear this band '
      + 'either: it is below human hearing and below what any speaker '
      + 'reproduces, so it is something to watch rather than to listen to.',
  },
  {
    lo: 100, hi: 580, label: 'Salmon hearing',
    kind: 'hearing',
    meaning: 'Roughly the range Atlantic salmon can detect, most sensitive '
      + 'around 100–200 Hz. Sound outside this band is not silent to the fish '
      + 'so much as simply not heard.',
  },
  {
    lo: 30, hi: 470, label: 'Cod hearing',
    kind: 'hearing',
    meaning: 'Atlantic cod hear lower and rather better than salmon, and are '
      + 'common around farm sites. A wild population can be affected by noise '
      + 'the stock does not react to.',
  },
]

/* Sources: what is making the sound. */
export const SOURCES: Band[] = [
  {
    lo: 10, hi: 1000, label: 'Vessels',
    kind: 'source',
    meaning: 'Propellers, engines and gearboxes. Loudest between about 50 and '
      + '150 Hz — which sits squarely inside the range salmon and cod hear '
      + 'best. This is the overlap that matters at a farm.',
  },
  {
    lo: 1000, hi: 20000, label: 'Wind, rain and surface',
    kind: 'source',
    meaning: 'Weather. Broad, steady, and rising with wind — the background '
      + 'the sea makes on its own, against which everything else is judged.',
  },
  {
    lo: 2000, hi: 24000, label: 'Snapping shrimp',
    kind: 'source',
    meaning: 'Sharp, dense clicking from invertebrates on the bottom. '
      + 'Biological, not a fault, and usually the loudest thing on a quiet '
      + 'night in shallow water.',
  },
]

export const ALL_BANDS: Band[] = [...SOURCES, ...HEARING]

/* ---------------------------------------------------------------- presets
 *
 * What the player offers as a band to listen through.
 *
 * Every number here is read off a Band above rather than typed in again. That
 * is not tidiness: it means a preset cannot drift from the range it claims to
 * be, and it means there is exactly one place where a figure would have to be
 * corrected if the literature moved.
 *
 * ---------------------------------------------------------------------------
 * WHERE THE FEEDING BAND CAME FROM, WHICH IS NOT FROM THIS SYSTEM
 *
 * `FEED_BAND` is 1-16 kHz on Phoniq's own knowledge of the equipment, given
 * 2026-08-12. It is written down as that rather than presented as a
 * measurement, because nothing in any of the three repositories has ever
 * recorded a feeding machine: not this file, not the node's analysis code, not
 * a report. The nearest thing in the codebase is a comment in
 * phoniq-streamer's spectrum.py bracketing "vessel blade rate and engine
 * harmonics (80-600 Hz)", and it says of itself that it is rationale for
 * choosing band edges rather than an observation.
 *
 * Operator knowledge is a real source and better than a guess. It is not the
 * same kind of thing as a measurement, and the difference should survive in
 * writing, so that whoever contradicts it later knows what they are
 * contradicting.
 *
 * CONFIRMING IT COSTS ONE HOUR. Record once with the feeder running and once
 * without, same site, same hour. Both clips are stored. The difference of
 * their spectra is the band, and `computeSpectrogram` in dsp.ts already does
 * the hard part.
 *
 * TWO HONEST LIMITS ON THIS PRESET, both stated to the listener:
 *
 * 1. The band is wide, and it is not exclusively the feeder. Wind and rain run
 *    1-20 kHz and snapping shrimp 2-24 kHz, so on a blowy night or a warm
 *    shallow one this preset passes weather and invertebrates too. It narrows
 *    the search; it does not identify anything on its own.
 *
 * 2. Its top is above what the stream carries. Live and the archive proxy are
 *    both 64 kbit AAC, whose psychoacoustic cut sits near 14 kHz -- so the top
 *    two of the sixteen are simply not in those bytes. Only a 24-bit original
 *    holds them. Nothing here can recover that, and pretending otherwise is
 *    exactly the failure this file exists to avoid.
 */

export interface Preset {
  /** Stable key; the label is translated at the point of use. */
  id: 'all' | 'fish' | 'feed' | 'custom'
  /** Null means no filtering: the whole band, as recorded. */
  band: { lo: number; hi: number } | null
}

/** The span a listener can tune across.
 *
 *  20 Hz is the bottom of every hydrophone in the fleet. The top used to be
 *  10 kHz -- the A5's calibrated linear range, above which it rolls off -- and
 *  that was right while every band on offer sat inside it. `FEED_BAND` does
 *  not, so an axis stopping at 10 kHz would have shown a preset running off
 *  the edge of its own picture.
 *
 *  Uncalibrated is not the same as unavailable: the sensor still records above
 *  10 kHz and the node's own contract says only that such content "must be
 *  reported as uncalibrated". This page reports no levels at all, so there is
 *  nothing here for the missing calibration to make false -- frequency is a
 *  property of the signal and survives it intact. */
export const TUNABLE = { lo: 4, hi: 16_000 }

/** Where a 64 kbit AAC stops carrying anything, roughly. Live and the archive
 *  proxy are both encoded at that rate, so a band reaching past this is asking
 *  for content the codec removed. Drawn on the strip rather than clamped away:
 *  the same band on a 24-bit original is complete, and which one you are
 *  listening to is not something to hide. */
export const CODEC_CEILING_HZ = 14_000

const zone = (label: string) => HEARING.find((b) => b.label === label)!

/** Salmon. Read off HEARING so the preset and the label cannot disagree. */
export const FISH_BAND = (() => {
  const salmon = zone('Salmon hearing')
  return { lo: salmon.lo, hi: salmon.hi }
})()

/** What `Fisken` is claiming, in the words this file already uses. Shown
 *  behind a quiet expander: a preset that claims to be the fish's ear owes the
 *  reader the strength of that claim, and this file's own header is careful
 *  that these ranges orient rather than prove. */
export const FISH_MEANING = zone('Salmon hearing').meaning

/** Infrasound. 5-10 Hz, and nobody hears it -- see the note on the band. */
export const INFRA_BAND = (() => {
  const infra = zone('Infrasound')
  return { lo: infra.lo, hi: infra.hi }
})()

export const INFRA_MEANING = zone('Infrasound').meaning

/** The feeding machine. Phoniq's own figure, 2026-08-12; see the header for
 *  what that is and is not. */
export const FEED_BAND = { lo: 1_000, hi: 16_000 }

export const FEED_MEANING =
  'Phoniq’s own figure for a feeding machine, not something this system has '
  + 'measured. The band is wide and it is not only the feeder: wind, rain and '
  + 'snapping shrimp all live inside it, so this narrows where to listen '
  + 'rather than identifying anything on its own. Live and the day-old archive '
  + 'are both 64 kbit, which stops carrying anything around 14 kHz — the top '
  + 'of this band exists only in a full-rate original.'

/** Bands that overlap the frequency range this recording can actually show.
 *
 *  A band above Nyquist is not "not present", it is unmeasurable — drawing it
 *  would put a label on a region of the picture that contains no information.
 */
export function visibleBands(sampleRate: number): Band[] {
  const nyq = sampleRate / 2
  return ALL_BANDS.filter((b) => b.lo < nyq)
    .map((b) => ({ ...b, hi: Math.min(b.hi, nyq) }))
}

/* ---------------------------------------------------------------------------
 * THE LEVEL HALF
 *
 * The header of this file says the zones above carry no level thresholds,
 * because until a mission is calibrated a threshold drawn against dBFS is
 * arithmetic dressed as science. It also says: "When `calibration.absolute`
 * becomes true the level half can be built, and this is the file it belongs
 * in." This is that half, and every one of the header's conditions still
 * applies — nothing below may be drawn on an uncalibrated mission.
 *
 * SOURCE. Havforskningsinstituttet / FHF 901744, "Støy som mulig stressfaktor
 * og velferdsutfordring i oppdrettsnæringen" (sluttrapport, 2025). The figures
 * are read off that report and nothing here is interpolated between them.
 *
 * WHAT THE REPORT ACTUALLY SAYS, because the difference matters:
 *
 *   <= 135 dB      "antas ikke å gi betydelige negative konsekvenser"
 *   <= 157 dB      (1 s SPL RMS) "kan gi negative konsekvenser avhengig av
 *                  varighet og nærhet"
 *   >  175 dB      (spisstrykk) "hørseltap og fysiske skader"
 *
 * and, in the same report, in as many words: **"Terskelverdier for skade er
 * ikke kjent."** These are observation ranges from a literature review, not
 * regulatory limits, and they are not thresholds at which something switches
 * on. Anything drawing them owes the reader that sentence, which is why
 * WELFARE_CAVEAT exists and is not optional decoration.
 *
 * Three consequences for the UI, all of them load-bearing:
 *
 *  1. NO VERDICT. The scale may show where a level sits. It may not say the
 *     farm is fine, or that it is not. A green tick against 135 dB would be a
 *     regulatory finding this system cannot support, delivered to somebody who
 *     would act on it.
 *  2. COLOUR AND A FACE, WHICH IS A DECISION THE OPERATOR TOOK. This file
 *     originally said the opposite: no traffic light, because colour running
 *     green-amber-red converts a range into a verdict without a word being
 *     written. That objection was raised and overruled on 2026-08-13, and the
 *     reasoning for it is worth keeping rather than deleting.
 *
 *     What makes it defensible is that the three states report the REPORT's
 *     own characterisation and not Phoniq's. HI says of these ranges "antas
 *     ikke a gi betydelige negative konsekvenser", "kan gi negative
 *     konsekvenser", and "horselstap og fysiske skader". A yellow band at 135
 *     is that sentence in a colour. It would not be defensible to colour
 *     thresholds we had chosen ourselves.
 *
 *     What remains a real risk, and is not solved by anything below: the level
 *     under the colour is an ESTIMATE, and on this fleet it could be some way
 *     out. A red fish is a strong claim to hang on a datasheet figure. The
 *     scale prints its ANSLAG badge beside the number for exactly that reason,
 *     and when a bench calibration lands the badge goes and the colour means
 *     more than it does today.
 *  3. THE INSTRUMENT'S OWN CEILING IS DRAWN TOO. On this fleet the top two
 *     zones are mostly above what the recorder can represent, and a scale that
 *     shows 175 dB without showing that we cannot see it invites the reading
 *     "we are nowhere near that" from a picture that means "we would not know".
 */

/** The band the report recommends welfare levels be measured in.
 *
 *  §7. Levels quoted against the zones below are levels in this band, not
 *  broadband — a broadband figure includes energy the report's numbers do not
 *  and reads high against them, which would overstate rather than understate.
 *
 *  Below about 20 Hz the hydrophone is outside the ±4 dB window its datasheet
 *  quotes, so the bottom of this band carries more uncertainty than the rest.
 *  It is a fraction of a decibel against an offset uncertainty measured in
 *  tens, and it is noted rather than corrected for. */
export const WELFARE_BAND_HZ = { lo: 5, hi: 500 }

export interface WelfareZone {
  /** Lower edge, dB re 1 µPa. The top edge is the next zone's floor. */
  from: number
  /** Drawn on the scale at `from`. Empty for the bottom zone, which has no
   *  lower edge to label. */
  edge: string
  /** What the report says about the range starting here. Quoted, not
   *  paraphrased — a paraphrase of a hedge tends to lose the hedge. */
  says: string
}

/** The report's ranges, lowest first. */
export const WELFARE_ZONES: WelfareZone[] = [
  {
    from: -Infinity,
    edge: '',
    says: 'Not assumed to cause significant negative effects.',
  },
  {
    from: 135,
    edge: '135',
    says: 'May have negative effects, depending on how long it lasts and how '
      + 'close the fish are.',
  },
  {
    from: 157,
    edge: '157',
    says: 'Above the range the report describes for sustained sound. Its '
      + 'figures stop here and the next one it gives is about peak pressure.',
  },
  {
    from: 175,
    edge: '175',
    says: 'Peak pressures above this are associated with hearing loss and '
      + 'physical injury.',
  },
]

/** The sentence that has to travel with the zones wherever they are drawn. */
export const WELFARE_CAVEAT =
  'Observation ranges from the Institute of Marine Research, not limits. The '
  + 'report states that thresholds for injury are not known.'

/** The ends of the drawn scale, dB re 1 µPa.
 *
 *  Fixed, and that is the point of it. The frequency strip auto-ranges so a
 *  quiet sea still fills the picture; a welfare scale that did the same would
 *  move 135 dB up and down the page and mean nothing. A level meter earns its
 *  keep by having the same geometry every time you look at it.
 *
 *  60 is below anything this fleet has recorded at night; 180 is above the
 *  report's highest figure. Neither is a measurement. */
export const WELFARE_SCALE = { lo: 60, hi: 180 }

/** Which zone a level falls in. */
export function welfareZone(spl: number): WelfareZone {
  let out = WELFARE_ZONES[0]!
  for (const z of WELFARE_ZONES) if (spl >= z.from) out = z
  return out
}

/** The plainest true sentence about a frequency, for the pointer readout. */
export function describeHz(f: number): string {
  const heard = HEARING.filter((b) => f >= b.lo && f <= b.hi)
    .map((b) => b.label.replace(' hearing', ''))
  const src = SOURCES.find((b) => f >= b.lo && f <= b.hi)
  const parts: string[] = []
  if (src) parts.push(src.label.toLowerCase())
  if (heard.length) parts.push(`heard by ${heard.join(' and ')}`)
  // Deliberately not "inaudible to fish": absence from a published range is
  // weak evidence, and overstating it is the same error as overstating harm.
  else parts.push('outside the ranges salmon and cod are known to hear')
  return parts.join(' · ')
}
